import {
  createContext,
  FunctionComponent,
  ReactElement,
  useMemo,
  useState,
  useEffect,
} from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { Stack, useToast } from "@chakra-ui/react";

import Api from "src/Config";

import { Switch } from "src/components";
import { textTruncate } from "src/helpers/truncateText";
import { Tooltip } from "@chakra-ui/react";

export const RadarTransactionRulesContext = createContext(null);

type Props = {
  children: ReactElement;
};

const RadarTransactionRulesProvider: FunctionComponent<Props> = ({
  children,
}) => {
  const { search } = useLocation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const queryParam = new URLSearchParams(search);
  const ruleType = queryParam.get("q").split("-");
  const ruleId = queryParam.get("id");
  const [pageNumber, setPageNumber] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [response, setResponse] = useState();

  const {
    mutateAsync,
    data: ruleData,
    isPending,
    // error: errorRule,
  } = useMutation({
    mutationFn: async (payload: any) => {
      const data = await Api.post("tm/rules", payload);
      return data;
    },
    onError: (err) => {
      console.log(err, "###");
    },
  });

  const {
    mutateAsync: mutateAsyncActivate,
    // data: ruleData,
    isPending: isPendingUpdate,
    // error: errorRule,
  } = useMutation({
    mutationFn: async (payload: any) => {
      const data = await Api.put(`tm/rules/${payload.rule_id}`, payload);
      return data;
    },
    onError: (err) => {
      console.log(err, "###");
    },
  });

  const { data, isLoading, error, isFetching, refetch } = useQuery({
    queryKey: ["tm_attributes"],
    // staleTime: 30000,
    // enabled: false,
    queryFn: async () => {
      const {
        data: { data },
      } = await Api.get(`tm/rules/attributes`);

      return data;
    },
  });

  const {
    data: viewSingleRuleData,
    isLoading: isLoadingSingleRuleData,
    error: singleRuleError,
    isFetching: isFetchingSingleRule,
    refetch: singleRuleRefetch,
  } = useQuery({
    queryKey: ["view_single_rule"],
    // staleTime: 30000,
    enabled: !!ruleId,
    queryFn: async () => {
      const {
        data: { data },
      } = await Api.get(`tm/rules/${ruleId}`);

      return data;
    },
  });

  // console.log(data && data, "123");

  const handleUpdateRuleAction = async (e: any, id: any) => {
    const payload = { rule_id: id, is_active: e.target.checked };
    try {
      const response = await mutateAsyncActivate(payload);

      if (response.status === 200) {
        queryClient.invalidateQueries({ queryKey: ["tm_rules"] });
        toast({
          description: response.data.detail || "Rule updated successfully",
          status: "success",
          duration: 9000,
          isClosable: true,
          variant: "left-accent",
        });
      }
    } catch (error: any) {
      const err: string = error?.response?.data.detail
        ? error?.response?.data?.detail
        : "An error occurred, Please try again";
      toast({
        description: err,
        status: "error",
        duration: 9000,
        isClosable: true,
        variant: "left-accent",
      });
    }
  };

  const {
    mutateAsync: mutateRuleDelete,
    // data: ruleData,
    isPending: isPendingDelete,
    // error: errorRule,
  } = useMutation({
    mutationFn: async (payload: any) => {
      const data = await Api.delete(`tm/rules/${payload.rule_id}`);
      return data;
    },
    onError: (err) => {
      console.log(err, "###");
    },
  });

  const {
    data: tmData,
    isLoading: isLoadingTmData,
    error: tmDataError,
    isFetching: isFetchingTmData,
  } = useQuery({
    queryKey: ["tm_rules", ruleType, pageNumber],
    // staleTime: 30000,
    queryFn: async () => {
      const url =
        ruleType[0] === "all"
          ? `tm/rules?page=${pageNumber + 1}`
          : `tm/rules?page=${pageNumber + 1}`;
      const { data } = await Api.get(`${url}`);

      const newResponse = data.results.map(
        (res: {
          id: string;
          name: string;
          created_at: string;
          // condition: string;
          condition_template: string;
          descriptions: string;
          is_active: boolean;
          rule_type: string;
          parameter_type: string;
          entity_attribute: string;
          entity_value: string;
          operator: string;
          conditions: any;
        }) => ({
          id: res.id,
          "on/off": (
            <Stack direction={"row"}>
              <Switch
                size="sm"
                color="#251169"
                handleChange={(e: any) => handleUpdateRuleAction(e, res.id)}
                defaultChecked={res.is_active}
              />
            </Stack>
          ),
          // "rule type": `${res.predefined ? "Default" : "Custom"}`,
          name: (
            <Tooltip label={res.name} placement="right-start">
              {`${textTruncate(res.name, 30) || "N/A"}`}
            </Tooltip>
          ),
          condition: (
            <Tooltip label={res.condition_template} placement="right-start">
              {textTruncate(res.condition_template, 30) || "N/A"}
            </Tooltip>
          ),
          "rule type": `${res.rule_type.toLowerCase()} Rule`,
          description: res.descriptions,
          ruleName: res.name,
          ruleParameter: res.parameter_type,
          ruleAttribute: res.entity_attribute,
          ruleValue: res.entity_value,
          ruleOperator: res.operator,
          conditions: res.conditions || [],
        }),
      );

      const response = {
        count: data.metadata.count,
        results: newResponse || [],
      };

      return response;
    },
  });

  useEffect(() => {
    if (tmData) {
      setResponse(tmData?.results);
      setPageCount(Math.ceil(tmData?.count / 10));
    }
  }, [tmData]);

  const handlePaginateRule = (e: any) => {
    setPageNumber(e.selected);
  };

  const contextValue = useMemo(
    () => ({
      // data,
      // isLoading,
      // isFetching,
      // error,
      isPending,
      mutateAsync,
      mutateAsyncActivate,
      isPendingUpdate,
      ruleData,
      tmData,
      data,
      handlePaginateRule,
      pageNumber,
      setPageNumber,
      isLoading,
      error,
      isFetching,
      refetch,
      pageCount,
      response,
      isLoadingTmData,
      tmDataError,
      isFetchingTmData,
      mutateRuleDelete,
      isPendingDelete,
      viewSingleRuleData,
      isLoadingSingleRuleData,
      singleRuleError,
      isFetchingSingleRule,
      singleRuleRefetch,
      handleUpdateRuleAction,
    }),
    [
      // isLoading,
      // data,
      // error,
      // isFetching,
      isPending,
      mutateAsync,
      mutateAsyncActivate,
      isPendingUpdate,
      ruleData,
      data,
      tmData,
      handlePaginateRule,
      pageNumber,
      setPageNumber,
      isLoading,
      error,
      isFetching,
      refetch,
      pageCount,
      response,
      isLoadingTmData,
      tmDataError,
      isFetchingTmData,
      mutateRuleDelete,
      isPendingDelete,
      viewSingleRuleData,
      isLoadingSingleRuleData,
      singleRuleError,
      isFetchingSingleRule,
      singleRuleRefetch,
      handleUpdateRuleAction,
    ],
  );
  return (
    <RadarTransactionRulesContext.Provider value={contextValue}>
      {children}
    </RadarTransactionRulesContext.Provider>
  );
};

export default RadarTransactionRulesProvider;
